import { useEffect, useRef, useState, useLayoutEffect  } from "react";
import { useCities } from "../providers/CitiesContext";
import EditComplexModal from "./admin/EditComplexModal";
import EditComplexButton from "./admin/EditComplexButton";
import "../styles/ComplexList.css";
import { formatTransferNumber } from "../utils/formatTransferNumber";

function ComplexList({
  complexes,                   // [] | null | undefined
  isLoading = false,           // ← прокинь из родителя во время запроса
  selectedComplexId,
  selectedCity,
  sidebarOpen,
  isAdmin,
  onLogout
}) {
  const containerRef = useRef(null);  
  const refs = useRef({});

  // ключ -> русское имя
  const { cityNameByKey } = useCities();
  const cityRu = cityNameByKey[String(selectedCity || "").toLowerCase()] || selectedCity || "";

  // “пустоту” показываем только когда загрузка завершилась и прошло немного времени
  const loading = isLoading || complexes == null; // null/undefined считаем загрузкой
  const [emptyReady, setEmptyReady] = useState(false);
  const [editingComplex, setEditingComplex] = useState(null);
  

  useEffect(() => {
    if (!loading && Array.isArray(complexes) && complexes.length === 0) {
      const t = setTimeout(() => setEmptyReady(true), 3000); // анти-мигание
      return () => clearTimeout(t);
    }
    setEmptyReady(false);
  }, [loading, complexes]);

  useEffect(() => {
    if (selectedComplexId && refs.current[selectedComplexId]) {
      refs.current[selectedComplexId].scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedComplexId]);
    // Зажимаем scrollTop, если он вылез за пределы после фильтрации/сжатия контента
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
    if (el.scrollTop > maxScrollTop) {
      el.scrollTop = maxScrollTop;
    }
  }, [complexes?.length]); // при изменении кол-ва карточек

  const className = `complexes-info ${sidebarOpen ? "open" : "closed"}`;

  // Скелетоны при загрузке
  if (loading) {
    return (
      <div className={className}>
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  // Заглушка (нет ЖК)
  if (emptyReady) {
    return (
      <div className={className}>
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden>🏙️</div>
          <h3 className="empty-state__title">
            В {cityRu ? `городе ${cityRu}` : "выбранном городе"} пока пусто
          </h3>
          <p className="empty-state__text">
            ❌ Все ЖК в стопе.
          </p>
        </div>
      </div>
    );
  }

// ---- helpers (вынеси выше компонента) ----
const dash = "—";

const joinOrDash = (v) =>
  Array.isArray(v) ? (v.length ? v.join(", ") : dash) : (v ?? dash);

const boolApts = (v) =>
  String(v).toLowerCase() === "true" || v === true;

const fmtNum = (n) => {
  if (n === null || n === undefined || n === "") return dash;
  const num = Number(n);
  return Number.isFinite(num)
    ? num.toLocaleString("ru-RU", { maximumFractionDigits: 1 })
    : String(n);
};

const Field = ({ label, value }) => (
  <p><strong>{label}:</strong> {value ?? dash}</p>
);

const isComplexActive = (complex) => {
  const status = complex?.status;
  return status === true || String(status).trim().toLowerCase() === "true";
};

// Безопасный вывод цен
function PricesBlock({ prices }) {
  const hasData =
    prices && typeof prices === "object" && Object.keys(prices).length > 0;

  if (!hasData) return <p><strong>Цены:</strong> нет данных</p>;

  return (
    <>
      <p><strong>Цены:</strong></p>
      {Object.entries(prices).map(([section, list], sIdx) => {
        const safeList = Array.isArray(list)
          ? list.filter((p) => p && typeof p === "object")
          : [];

        return (
          <div key={section || `sec-${sIdx}`}>
            <strong>{section || dash}</strong>
            <ul>
              {safeList.length === 0 ? (
                <li>{dash}</li>
              ) : (
                safeList.map((p, i) => {
                  const type  = p.type ?? dash;
                  const area  = p.area_from_m2 ?? p.area ?? null;
                  const price = p.price_from_mln ?? p.price_mln ?? null;
                  return (
                    <li key={`${section || "sec"}-${i}`}>
                      {type}{" "}
                      {area != null ? <>— от {String(area)} м²</> : <>— {dash}</>}
                      {" — "}
                      {price != null ? <>от {fmtNum(price)} млн руб.</> : dash}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        );
      })}
    </>
  );
}

  // Основной рендер
  return (
    <div className={className} ref={containerRef}>
      {complexes.map((complex) => {
        const isInactive = !isComplexActive(complex);

        return (
          <div
            className={`complex-card${isInactive ? " complex-card--inactive" : ""}`}
            key={complex?._id ?? crypto.randomUUID?.() ?? Math.random()}
            ref={(el) => {
              if (complex?._id) refs.current[complex._id] = el;
            }}
          >
            <div className="complex-card__header">
              <h2>{complex?.name ?? "Без названия"}</h2>
              <div className="complex-card__header-actions">
                {isInactive && <span className="complex-status-badge">Стоп</span>}
                {isAdmin && <EditComplexButton onClick={() => setEditingComplex(complex)} />}
              </div>
            </div>
            {complex?.category === "special" || complex?.category === "vehicles" ? (
              <>
                <div dangerouslySetInnerHTML={{ __html: complex?.info ?? "" }} />
              </>
            ) : complex?.category === "commercial" ? (
              <>
                <p>
                  <strong>Тип перевода:</strong>{" "}
                  <span dangerouslySetInnerHTML={{ __html: complex?.type ?? "" }} />
                </p>
                <Field label="Номер перевода" value={formatTransferNumber(complex?.transfer_number) || dash} />
                <Field label="Срок сдачи" value={joinOrDash(complex?.completion_dates)} />
                <Field label="Застройщик" value={complex?.developer} />
                <Field label="Расположение" value={complex?.location} />
                <div dangerouslySetInnerHTML={{ __html: complex?.info ?? "" }} />
                <Field label="Время работы" value={complex?.working_hours} />
              </>
            ) : (
              <>
                <p>
                  <strong>Тип перевода:</strong>{" "}
                  <span dangerouslySetInnerHTML={{ __html: complex?.type ?? "" }} />
                </p>
                <Field label="Номер перевода" value={formatTransferNumber(complex?.transfer_number) || dash} />
                <Field label="Срок сдачи" value={joinOrDash(complex?.completion_dates)} />
                <Field label="Застройщик" value={complex?.developer} />
                <Field label="Расположение" value={complex?.location} />
                <Field label="Метро" value={complex?.metro} />

                {boolApts(complex?.isApartments) && (
                  <p><strong>Статус помещений:</strong> Апартаменты</p>
                )}

                <PricesBlock prices={complex?.prices} />

                <Field label="Отделка" value={joinOrDash(complex?.finishes_available)} />
                <Field label="Время работы" value={complex?.working_hours} />
              </>
            )}
          </div>
        );
      })}

      <EditComplexModal
        complex={editingComplex}
        isOpen={Boolean(editingComplex)}
        onClose={() => setEditingComplex(null)}
        onSaved={() => {
          setEditingComplex(null);
        }}
      />
    </div>
  );

}

function SkeletonCard() {
  return (
    <div className="complex-card skeleton">
      <div className="sk-line sk-title" />
      <div className="sk-line" />
      <div className="sk-line" />
      <div className="sk-line short" />
    </div>
  );
}

export default ComplexList;
